import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, HelpCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { PrestationForm } from "@/components/prestations/PrestationForm";
import { api } from "@/lib/api";
import { usePeriod } from "@/lib/period";
import { useToast } from "@/components/ui/Toast";
import { TONE_STYLES, type Tone } from "@/lib/colors";
import { cn, formatDate, formatEUR, todayISO } from "@/lib/utils";
import {
  type Prestation,
  type PrestationStatus,
} from "@shared/schema";

const STATUS_LABELS: Record<PrestationStatus, string> = {
  facture: "Facturée",
  paye: "Payée",
  annule: "Annulée",
};

const STATUS_TONE: Record<PrestationStatus, Tone> = {
  facture: "yellow",
  paye: "green",
  annule: "slate",
};

type Filter = "all" | "facture" | "paye";

export function PrestationsPage() {
  const [items, setItems] = useState<Prestation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Prestation | null>(null);
  const toast = useToast();
  const { inPeriod } = usePeriod();

  // Prestations de la période (par date de facturation).
  const scoped = useMemo(
    () => items.filter((it) => inPeriod(it.invoicedAt)),
    [items, inPeriod],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { prestations } = await api.listPrestations();
      setItems(prestations);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totals = useMemo(() => {
    const t = { invoiced: 0, pending: 0, paid: 0 };
    for (const it of scoped) {
      if (it.status === "annule") continue;
      t.invoiced += it.amount;
      if (it.status === "paye") t.paid += it.amount;
      else if (it.status === "facture") t.pending += it.amount;
    }
    return t;
  }, [scoped]);

  const filtered = useMemo(() => {
    if (filter === "all") return scoped;
    return scoped.filter((it) => it.status === filter);
  }, [scoped, filter]);

  async function markPaid(p: Prestation) {
    try {
      await api.updatePrestation(p.id, {
        status: "paye",
        paidAt: todayISO(),
      });
      toast.success("Prestation marquée payée");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  async function markUnpaid(p: Prestation) {
    try {
      await api.updatePrestation(p.id, {
        status: "facture",
        paidAt: null,
      });
      toast.success("Statut réinitialisé");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  async function remove(p: Prestation) {
    if (!confirm(`Supprimer la prestation pour ${p.clientName} ?`)) return;
    try {
      await api.deletePrestation(p.id);
      toast.success("Prestation supprimée");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  return (
    <>
      <PageHeader
        title="Prestations"
        subtitle="Factures, créances et autres revenus hors commission Power Coaching"
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={16} /> Nouvelle prestation
          </button>
        }
      />

      <div className="grid grid-cols-3 max-sm:grid-cols-1 gap-3 mb-5">
        <KpiCard tone="navy" label="Facturé" value={formatEUR(totals.invoiced)} />
        <KpiCard tone="yellow" label="En attente" value={formatEUR(totals.pending)} />
        <KpiCard tone="green" label="Payé" value={formatEUR(totals.paid)} />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            ["all", "Toutes"],
            ["facture", "En attente"],
            ["paye", "Payées"],
          ] as Array<[Filter, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              "btn btn-sm",
              filter === key ? "btn-primary" : "btn-outline",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="surface p-8 text-center text-sm text-[var(--color-muted-foreground)]">
          Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="surface p-10 text-center">
          <p className="text-[var(--color-muted-foreground)]">
            Aucune prestation à afficher.
          </p>
        </div>
      ) : (
        <div className="surface overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Client</th>
                <th>Description</th>
                <th>Facturé le</th>
                <th>Échéance</th>
                <th className="text-right">Montant</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const tone = TONE_STYLES[STATUS_TONE[p.status]];
                return (
                  <tr key={p.id}>
                    <td>
                      <div className="font-medium text-[var(--color-navy-700)]">
                        {p.clientName}
                      </div>
                      {p.clientEmail && (
                        <div className="text-xs text-[var(--color-muted-foreground)] truncate max-w-[200px]">
                          {p.clientEmail}
                        </div>
                      )}
                    </td>
                    <td className="text-sm">
                      {p.description || (
                        <span className="text-[var(--color-muted-foreground)]">—</span>
                      )}
                    </td>
                    <td className="text-sm tabular">
                      {formatDate(p.invoicedAt)}
                    </td>
                    <td className="text-sm tabular">
                      {p.dueAt ? (
                        formatDate(p.dueAt)
                      ) : (
                        <span className="text-[var(--color-muted-foreground)]">—</span>
                      )}
                    </td>
                    <td className="text-right font-semibold tabular">
                      {formatEUR(p.amount)}
                    </td>
                    <td>
                      <span className={cn("pill", tone.pillBg, tone.pillFg)}>
                        <span className={cn("pill-dot", tone.dot)} />
                        {STATUS_LABELS[p.status]}
                        {p.status === "paye" && p.paidAt
                          ? ` ${formatDate(p.paidAt)}`
                          : ""}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1.5">
                        {p.status === "facture" ? (
                          <button
                            type="button"
                            className="btn btn-success-outline btn-sm"
                            onClick={() => markPaid(p)}
                            title="Marquer comme encaissé"
                          >
                            <HelpCircle size={13} /> Encaissé ?
                          </button>
                        ) : p.status === "paye" ? (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={() => markUnpaid(p)}
                            title="Encaissé — cliquer pour annuler"
                          >
                            <Check size={13} /> Encaissé
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          aria-label="Éditer"
                          onClick={() => setEditing(p)}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          aria-label="Supprimer"
                          onClick={() => remove(p)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nouvelle prestation"
        size="lg"
      >
        <PrestationForm
          onSubmit={async (values) => {
            await api.createPrestation(values);
            toast.success("Prestation créée");
            setCreateOpen(false);
            refresh();
          }}
          submitLabel="Créer"
        />
      </Modal>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Éditer la prestation"
        size="lg"
      >
        {editing && (
          <PrestationForm
            initial={editing}
            onSubmit={async (values) => {
              await api.updatePrestation(editing.id, values);
              toast.success("Prestation mise à jour");
              setEditing(null);
              refresh();
            }}
            submitLabel="Sauvegarder"
          />
        )}
      </Modal>
    </>
  );
}

function KpiCard({
  tone,
  label,
  value,
}: {
  tone: "navy" | "yellow" | "green";
  label: string;
  value: string;
}) {
  const accent = TONE_STYLES[tone].border;
  return (
    <div
      className={cn(
        "surface p-4 border-l-4 max-sm:flex max-sm:items-center max-sm:justify-between max-sm:gap-3 max-sm:py-3",
        accent,
      )}
    >
      <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)] font-medium">
        {label}
      </div>
      <div className="text-xl font-bold tabular text-[var(--color-navy-700)] mt-1 max-sm:mt-0">
        {value}
      </div>
    </div>
  );
}
